import React, { useCallback, useMemo } from 'react';
import {
  HiddenContent,
  SideNavLinkInnerWrapper,
  SideNavLinkOuterWrapper,
  SideNavLinkTitleWrapper,
  SvgWrapper,
} from './styles';
import { SvgIcon } from 'src/commons/components/SvgIcon/SvgIcon';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import SideNavLinkFeatures from 'src/features/nav/components/ConsolidatedSideNav/SideNavLinkFeatures/SideNavLinkFeatures';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { CC_MODAL_TOOLBAR_ID } from 'src/commons/constants';

interface SideNavLinkProps {
  id: string;
  isQuickNavModalEnabled?: boolean;
}

const SideNavLink: React.VFC<SideNavLinkProps> = ({
  id,
  isQuickNavModalEnabled = false,
}) => {
  const { isNavOpen, selectedNavItemId, toolbarItems } =
    useConsolidatedNavContext();

  const isSelected = useMemo(
    () =>
      selectedNavItemId === id ||
      (selectedNavItemId === CC_MODAL_TOOLBAR_ID &&
        id === 'aac-toolbar-item-sponsored-ads' &&
        !isQuickNavModalEnabled),
    [selectedNavItemId],
  );
  const toolbarItem = useMemo(
    () => toolbarItems.find((toolbarItem) => toolbarItem.id === id),
    [toolbarItems],
  );

  const onClickHandler = useCallback(() => {
    NavMetricPublisher.publishCounterMonitor(`toolbarItemClicked-${id}`, 1);
  }, [id]);

  if (!toolbarItem) {
    return null;
  }

  const { link, id: linkId } = toolbarItem.sidebarGroups[0].links[0];
  const { label: title, url } = link;

  return (
    <SideNavLinkOuterWrapper isSelected={isSelected}>
      <SideNavLinkInnerWrapper
        isNavOpen={isNavOpen}
        isSelected={isSelected}
        href={url}
        data-ccx-e2e-id={linkId}
        aria-label={title}
        onClick={onClickHandler}
        {...(isSelected && { ariaCurrent: 'page' })}
        {...getTaktAttributes(linkId, chromeComponents.NAV, { url: url })}
      >
        <SvgWrapper
          isSelected={isSelected}
          data-ccx-e2e-id={id}
          alignItems="center"
          justifyContent="center"
        >
          <SvgIcon
            iconPath={toolbarItem.iconPath}
            ariaLabel={title}
            isSelected={isSelected}
          />
        </SvgWrapper>
        <HiddenContent alignItems="center" justifyContent="space-between">
          <SideNavLinkTitleWrapper>{title}</SideNavLinkTitleWrapper>
          <SideNavLinkFeatures
            sideNavLink={toolbarItem.sidebarGroups[0].links[0]}
            isFocusable
          />
        </HiddenContent>
      </SideNavLinkInnerWrapper>
    </SideNavLinkOuterWrapper>
  );
};

export default SideNavLink;
